// Auth-layout shell — hero panel on the left (logo, headline, value
// bullets, testimonials, footer) and the active wizard step on the
// right (mountains backdrop with the step card on top). Styling is
// hand-aligned with the marketing site's signup.html so the visual
// transition from backcountrybroadband.com/signup → signup.bcbb.io is
// imperceptible. The per-stage header/subheader/body + testimonials
// list are admin-configurable via WizardConfig — see
// apps/api/src/schemas/wizard-config.ts and the Signup Wizard editor.

import type { ReactNode } from 'react';
import { sanitizeHtml } from '../lib/sanitizeHtml';

export interface TestimonialDisplay {
  id: string;
  quote: string;
  author: string;
  subtitle?: string;
  stars: number;
}

interface ShellProps {
  children: ReactNode;
  /** Form-card header. Falls back to nothing when unset. */
  header?: string;
  /** Short copy under the header. */
  subheader?: string;
  /** Longer copy under the subheader; sanitized HTML. */
  body?: string;
  /** Testimonial cards rendered in the hero panel. Empty array → none. */
  testimonials?: TestimonialDisplay[];
}

export function Shell({ children, header, subheader, body, testimonials }: ShellProps) {
  return (
    <main className="grid min-h-screen grid-cols-1 md:grid-cols-2">
      <aside
        className="relative flex flex-col gap-8 overflow-hidden p-12 text-white"
        style={{
          background: 'linear-gradient(135deg, var(--bcbb-ink) 0%, var(--bcbb-primary) 100%)',
        }}
      >
        {/* Cyan radial halo lifted from marketing signup.html. */}
        <div
          aria-hidden
          className="pointer-events-none absolute -right-[30%] -top-[10%] h-[720px] w-[720px]"
          style={{
            background: 'radial-gradient(closest-side, rgba(193,231,239,.32), transparent 70%)',
          }}
        />
        <header className="relative">
          <a
            href="/"
            aria-label="Backcountry Broadband home"
            className="inline-block transition-opacity hover:opacity-80"
          >
            <img src="/logo-wordmark-on-dark.png" alt="Backcountry Broadband" className="h-28" />
          </a>
        </header>
        <section className="relative">
          <p className="mb-4 text-xs font-bold uppercase tracking-[.14em] text-[var(--bcbb-cyan)]">
            The Canyon's Best Internet&trade;
          </p>
          <h1 className="m-0 mb-5 text-4xl font-bold leading-[1.05] tracking-tight md:text-5xl">
            Symmetrical fiber.
            <br />
            No data caps.
            <br />
            <span
              className="relative"
              style={{
                background:
                  'linear-gradient(92deg, var(--bcbb-cyan) 0%, #ffffff 38%, #ffe6c8 62%, var(--bcbb-comms) 100%)',
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                color: 'transparent',
              }}
            >
              Right here in the canyon.
            </span>
          </h1>
          <p className="m-0 mb-7 max-w-lg text-base text-white/80">
            Self-serve signup - tell us where you are, pick a plan, and we'll set up your account.
          </p>
          <ul className="m-0 flex list-none flex-col gap-2.5 p-0 text-white/90">
            <BulletLine>Up to 10 Gbps symmetrical speeds, no data caps</BulletLine>
            <BulletLine>Direct peering - sub-4&nbsp;ms to Denver</BulletLine>
            <BulletLine>Local support - we'll know your name</BulletLine>
          </ul>
        </section>

        {testimonials && testimonials.length > 0 && (
          <section
            aria-label="What customers say"
            className="relative flex flex-col gap-3"
          >
            <header className="text-xs font-bold uppercase tracking-[.14em] text-[var(--bcbb-cyan)]">
              What our neighbors say
            </header>
            {testimonials.map((t) => (
              <article
                key={t.id}
                className="rounded-xl border border-white/10 bg-white/[0.06] p-4 backdrop-blur-sm"
              >
                <blockquote className="m-0 text-sm leading-relaxed text-white/90">
                  &ldquo;{t.quote}&rdquo;
                </blockquote>
                <footer className="mt-3 flex items-center justify-between gap-3 text-xs text-white/70">
                  <span>
                    <span className="font-semibold text-white/90">{t.author}</span>
                    {t.subtitle ? <> &middot; {t.subtitle}</> : null}
                  </span>
                  <span aria-label={`${t.stars} out of 5 stars`} className="text-[var(--bcbb-comms)]">
                    {'★'.repeat(t.stars)}
                    <span className="text-white/20">{'★'.repeat(5 - t.stars)}</span>
                  </span>
                </footer>
              </article>
            ))}
          </section>
        )}

        <footer className="relative mt-auto flex items-center gap-5 text-xs text-white/55">
          <span>© Backcountry Broadband</span>
          <a
            href="https://backcountrybroadband.com/privacy.html"
            className="text-white/70 hover:text-white"
          >
            Privacy
          </a>
          <a
            href="https://backcountrybroadband.com/tos.html"
            className="text-white/70 hover:text-white"
          >
            Terms
          </a>
          {/* ToS v2.1 §1 incorporates the AUP and NMP by reference alongside
              the Privacy Policy. Linking only two of the three left a customer
              signing up unable to reach the rest of the agreement. */}
          <a
            href="https://backcountrybroadband.com/acceptable-use.html"
            className="text-white/70 hover:text-white"
          >
            Acceptable Use
          </a>
          <a
            href="https://backcountrybroadband.com/network-management.html"
            className="text-white/70 hover:text-white"
          >
            Network Policy
          </a>
        </footer>
      </aside>

      <section className="relative flex items-start justify-center overflow-y-auto p-12">
        {/* Mountains backdrop behind the form card. Same asset the
            marketing-site signup.html uses; copied into apps/portal/public/
            so Vite serves it from /mountains-canyon.jpg. */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(244,245,250,.55) 0%, rgba(244,245,250,.78) 100%), url('/mountains-canyon.jpg') center/cover no-repeat",
          }}
        />
        <div
          className="relative w-full max-w-2xl rounded-2xl border bg-white p-8 shadow-xl"
          style={{ borderColor: 'var(--bcbb-border)' }}
        >
          {(header || subheader || body) && (
            <header className="mb-6">
              {header && (
                <h2 className="m-0 text-2xl font-semibold tracking-tight">{header}</h2>
              )}
              {subheader && (
                <p className="mt-2 text-sm leading-relaxed text-[var(--bcbb-text-muted)]">
                  {subheader}
                </p>
              )}
              {body && (
                <div
                  className="prose prose-sm mt-3 max-w-none text-[var(--bcbb-text)]"
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(body) }}
                />
              )}
            </header>
          )}
          {children}
        </div>
      </section>
    </main>
  );
}

function BulletLine({ children }: { children: ReactNode }) {
  return (
    <li className="flex items-center gap-2.5">
      <span
        className="inline-flex h-6 w-6 items-center justify-center rounded-full text-sm font-bold text-[var(--bcbb-cyan)]"
        style={{ background: 'rgba(193,231,239,.18)' }}
        aria-hidden
      >
        ✓
      </span>
      {children}
    </li>
  );
}

/**
 * Pick testimonials from a global pool based on a stage's display
 * config. Random selection happens at call time, so each mount of the
 * shell varies (i.e., refresh the page → different quotes).
 */
export function selectTestimonials(
  pool: TestimonialDisplay[],
  config:
    | { mode: 'random' | 'static'; count: number; ids: string[] }
    | null
    | undefined,
): TestimonialDisplay[] {
  if (!config || pool.length === 0) return [];
  if (config.mode === 'static') {
    // Preserve admin-chosen order; drop ids that no longer exist.
    const byId = new Map(pool.map((t) => [t.id, t]));
    return config.ids.map((id) => byId.get(id)).filter((t): t is TestimonialDisplay => !!t);
  }
  // Random: Fisher-Yates a copy, slice.
  const copy = [...pool];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j]!, copy[i]!];
  }
  return copy.slice(0, Math.min(config.count, copy.length));
}
